/**
 * Pure helper for turning a clicked notification into an internal SPA target.
 *
 * `resolveTarget` decides whether a `target_url` is a same-origin internal
 * path (→ react-router navigation) or external (→ caller falls back to
 * window.location). Only the target_url's own query string is carried over —
 * `extra_params` are deliberately NOT serialised into the URL. `origin` is
 * injected so this stays window-free + testable.
 */

export interface InternalTarget {
  pathname: string;
  /** Leading-`?` query string, or '' when there are no params. */
  search: string;
}

/**
 * Resolve a notification `target_url` to an internal target, or null when
 * it's external / unparseable / non-http(s).
 */
export function resolveTarget(targetUrl: string | null | undefined, origin: string): InternalTarget | null {
  if (typeof targetUrl !== 'string' || !targetUrl.trim()) return null;
  let url: URL;
  try {
    url = new URL(targetUrl, origin);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (url.origin !== origin) return null;

  return { pathname: url.pathname, search: url.search };
}
